import { useForm } from '@tanstack/react-form';
import { createFileRoute } from '@tanstack/react-router';
import { Trash02 } from '@untitled-ui/icons-react';
import { useEffect, useState } from 'react';

import { ModalOwnerCantDelete } from '../-settings-components/modal-owner-cant-delete';
import { ModalUserDeleteAccount } from '../-settings-components/modal-user-delete-account';
import { Button } from '../../../../components/buttons/button';
import { TextField } from '../../../../components/inputs/text-field';
import { SelectField } from '../../../../components/select-field';
import { ToastVariant } from '../../../../components/toast';
import { Tooltip } from '../../../../components/tooltip';
import { OrganizationUserRole } from '../../../../entity/api/organization-user';
import { type User, userCreateSchema } from '../../../../entity/api/user';
import { addTrackCrumb, trackError } from '../../../../error/tracking';
import { type ErrorAny, mapErrorToTranslation } from '../../../../error/utils';
import { captureException } from '../../../../logger/capture-exception';
import { getQueryClient } from '../../../../sdk/request';
import { useAuthUser, useUpdateUser } from '../../../../services/storage';
import {
  fetchOrganizationUsersListImperative,
  fetchUserOrganizationsImperative,
  getAllOrganizationUsersFromCache,
} from '../../../../services/storage/organization-users-queries';
import { changeLanguage, tGlobal, tNamespace } from '../../../../services/translation';
import { useToast } from '../../../../shared/contexts/toast-context';
import { getLanguageSupportedOnBlym } from '../../../../shared/countries';
import { ownerOrgsHaveOtherUsers } from '../../../../shared/organization';
import { getUserDefaults } from '../../../../shared/user';
import { parseFirstZodError } from '../../../../shared/validation';

const t = tNamespace('app.settings.user');

export const Route = createFileRoute('/app/settings/_sidebar-settings-layout/user')({
  beforeLoad: function () {
    addTrackCrumb({
      category: 'route',
      message: '/app/settings/_sidebar-settings-layout/user',
      level: 'info',
    });
  },
  // eslint-disable-next-line no-use-before-define
  component: UserSettings,
});

function UserSettings() {
  // TanStack Query hook for auth user
  const { data: authUser } = useAuthUser();
  const [user, setUser] = useState<User | undefined>(undefined);
  const addToast = useToast();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isCantDeleteOpen, setIsCantDeleteOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isContentChanged, setIsContentChanged] = useState(false);

  // TanStack Query mutation
  const updateUserMutation = useUpdateUser();

  const form = useForm({
    defaultValues: getUserDefaults(user, true),
    onSubmit: function (data) {
      addToast({
        title: t('savingUser'),
        variant: ToastVariant.Loading,
        id: Date.now(),
      });

      updateUserMutation.mutate(
        {
          firstName: data.value.firstName,
          lastName: data.value.lastName,
          email: data.value.email,
          language: data.value.language,
        },
        {
          onSuccess: function () {
            addToast({
              title: t('userUpdated'),
              variant: ToastVariant.Success,
              id: Date.now(),
            });
            changeLanguage(data.value.language)
              .then(function () {
                //TODO: this shouldn't happen, we should be able to update the language everywhere without reloading the page
                window.location.reload();
              })
              .catch(function (err) {
                captureException(
                  err,
                  {
                    file: 'app/settings/_sidebar-settings-layout/user.tsx',
                    method: 'onSubmit',
                    infoMessage: 'language change failed, reloading anyway',
                  },
                  trackError
                );
                // Reload anyway to pick up the server-saved language
                window.location.reload();
              });
          },
          onError: function (err) {
            const errMsg = mapErrorToTranslation(err as unknown as ErrorAny);
            addToast({
              title: errMsg ?? t('errorSavingUser'),
              variant: ToastVariant.Error,
              id: Date.now(),
            });
            captureException(
              err,
              {
                file: 'app/settings/_sidebar-settings-layout/user.tsx',
                method: 'onSubmit',
                infoMessage: 'unable to update user',
              },
              trackError
            );
          },
        }
      );
    },
  });

  function onDeleteBtn() {
    if (isDeleteLoading || authUser == null) {
      return;
    }

    setIsDeleteLoading(true);

    // NOTE: deletion of user requires that owned organizations
    //       are empty, we need to check
    // we want to make sure that all the organization users are updated
    const queryClient = getQueryClient();
    fetchUserOrganizationsImperative(queryClient)
      .then(async function (orgs) {
        const ownerOrgIds: string[] = [];
        for (let i = 0; i < orgs.length; i += 1) {
          const { organizationId, role, userId } = orgs[i];
          if (role === OrganizationUserRole.Owner && userId === authUser?._id) {
            ownerOrgIds.push(organizationId);
          }
        }

        // user is not an owner of any organizations
        if (ownerOrgIds.length === 0) {
          setIsDeleteOpen(true);
          return;
        }

        // Fetch org users for each owned organization
        await Promise.allSettled(
          ownerOrgIds.map((id) => fetchOrganizationUsersListImperative(queryClient, id))
        );

        const allOrgUsers = getAllOrganizationUsersFromCache(queryClient);
        const ownerOrgWithUsersIds = ownerOrgsHaveOtherUsers(authUser, allOrgUsers);

        // check if we have other users on organizations we are owner
        if (ownerOrgWithUsersIds.length > 0) {
          setIsCantDeleteOpen(true);
        } else {
          setIsDeleteOpen(true);
        }
      })
      .catch(function (err) {
        captureException(
          err,
          {
            file: 'app/settings/_sidebar-settings-layout/user.tsx',
            method: 'onDeleteBtn',
            infoMessage: 'unable to get user organizations',
          },
          trackError
        );
      })
      .finally(function () {
        setIsDeleteLoading(false);
      });
  }

  // Sync user state with TanStack Query data
  useEffect(
    function () {
      if (authUser == null || user?._id === authUser._id) {
        return;
      }

      setUser(authUser);
      form.reset(getUserDefaults(authUser));
    },
    [authUser, user, form]
  );

  return (
    <div className="h-full flex-1 overflow-y-auto">
      <div className="mx-auto mb-0 flex w-full max-w-content flex-col p-4 md:mb-20 md:p-0 md:pt-12">
        <div className="mb-6 flex items-start justify-between">
          <p className="font-display-2">{t('title')}</p>
          <Button
            size="md"
            variant="accent"
            disabled={!isContentChanged}
            onClick={function () {
              void form.handleSubmit();
            }}
          >
            {tGlobal('save')}
          </Button>
        </div>
        <form>
          <div className="mb-6 flex gap-4">
            <form.Field
              name="firstName"
              validators={{
                onBlur: function (data) {
                  return parseFirstZodError(userCreateSchema.shape.firstName, data.value);
                },
              }}
              // eslint-disable-next-line react/no-children-prop
              children={function (field) {
                return (
                  <TextField
                    label={t('firstName')}
                    value={field.state.value}
                    placeholder={t('firstNamePlaceholder')}
                    onChange={function (e) {
                      field.handleChange(e.target.value);
                      setIsContentChanged(true);
                    }}
                    onBlur={field.handleBlur}
                    sizeField="lg"
                  />
                );
              }}
            />
            <form.Field
              name="lastName"
              validators={{
                onBlur: function (data) {
                  return parseFirstZodError(userCreateSchema.shape.lastName, data.value);
                },
              }}
              // eslint-disable-next-line react/no-children-prop
              children={function (field) {
                return (
                  <TextField
                    label={t('lastName')}
                    value={field.state.value}
                    placeholder={t('lastNamePlaceholder')}
                    onChange={function (e) {
                      field.handleChange(e.target.value);
                      setIsContentChanged(true);
                    }}
                    onBlur={field.handleBlur}
                    sizeField="lg"
                  />
                );
              }}
            />
          </div>
          <form.Field
            name="language"
            validators={{
              onBlur: function (data) {
                return parseFirstZodError(userCreateSchema.shape.language, data.value);
              },
            }}
            // eslint-disable-next-line react/no-children-prop
            children={function (field) {
              return (
                <SelectField
                  value={field.state.value}
                  onChange={function (e) {
                    field.handleChange(e);
                    setIsContentChanged(true);
                  }}
                  options={getLanguageSupportedOnBlym()}
                  size="lg"
                  contentFit={true}
                  className="w-full"
                  triggerClassName="w-full"
                  contentClassName="z-[100]"
                  label={tGlobal('language')}
                />
              );
            }}
          />
          <Tooltip text={t('emailTooltip')} side="top" delayDuration={100}>
            <div className="mt-6 w-full">
              <form.Field
                name="email"
                validators={{
                  onBlur: function (data) {
                    return parseFirstZodError(userCreateSchema.shape.email, data.value);
                  },
                }}
                // eslint-disable-next-line react/no-children-prop
                children={function (field) {
                  // TODO: enable this when business decides to allow users to change their email
                  return (
                    <TextField
                      label={t('email')}
                      disabled={true}
                      value={field.state.value}
                      placeholder={t('emailPlaceholder')}
                      onChange={function (e) {
                        field.handleChange(e.target.value);
                      }}
                      onBlur={field.handleBlur}
                      sizeField="lg"
                    />
                  );
                }}
              />
            </div>
          </Tooltip>
        </form>
        <Button
          size="sm"
          variant="destructive"
          className="mt-6 self-end"
          loading={isDeleteLoading}
          onClick={() => onDeleteBtn()}
          leftIcon={<Trash02 width={16} height={16} />}
        >
          {t('deleteAccount')}
        </Button>
      </div>
      <ModalUserDeleteAccount isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} />
      <ModalOwnerCantDelete isOpen={isCantDeleteOpen} onClose={() => setIsCantDeleteOpen(false)} />
    </div>
  );
}
